// src/AuthContext.js
// ============================================================
// Your Scholarship Finder — Auth Context
// ============================================================
// Manages JWT token state across the app.
// On mount, validates any stored token against /api/auth/me
// and checks for an existing student profile via /api/students/me.
// Provides login(), logout(), and studentId to child components.
// Sentry user context set on auth changes (#82, S51).
// ============================================================

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { API_URL } from './config';

const AuthContext = createContext(null);

const TOKEN_KEY = 'ss_token';

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [loading, setLoading]     = useState(true);
  const navigate = useNavigate();

  // --- Validate token + check for existing student profile ---
  const validateAndFetchProfile = useCallback(async (token) => {
    try {
      // 1. Validate token
      const authRes = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!authRes.ok) throw new Error('Token invalid');
      const authData = await authRes.json();
      setUser({ ...authData.user, token });

      // Sentry: identify user for error context
      Sentry.setUser({
        id: authData.user.id,
        email: authData.user.email,
        role: authData.user.role,
      });

      // 2. Use student_id from /me response (already queried server-side)
      if (authData.user?.student_id) {
        setStudentId(authData.user.student_id);
      }
      // 404 = no profile yet — that's fine, studentId stays null
    } catch (_) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setStudentId(null);
      Sentry.setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // --- On mount: validate any stored token ---
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    validateAndFetchProfile(token);
  }, [validateAndFetchProfile]);

  // --- Login: store token, set user, check for profile ---
  const login = useCallback((token, userData) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser({ ...userData, token });
    validateAndFetchProfile(token);
  }, [validateAndFetchProfile]);

  // --- Logout: clear everything and redirect to login ---
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setStudentId(null);
    Sentry.setUser(null);
    navigate('/login');
  }, [navigate]);

  // --- Called by ProfileForm after creating a new profile ---
  const setCurrentStudentId = useCallback((id) => {
    setStudentId(id);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      studentId,
      loading,
      login,
      logout,
      setCurrentStudentId,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
